import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input, Label } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/lib/auth';
import { callFinishExam, type CategoryId } from '@/lib/api';
import { friendly } from './SignIn';
import { cn } from '@/lib/utils';
import {
  Activity,
  Award,
  BookOpen,
  CheckCircle2,
  Clock,
  Lock,
  Sparkles,
  TrendingUp,
  Trophy,
  Zap,
} from 'lucide-react';

const CATEGORY_LABEL: Record<CategoryId, string> = {
  adl: 'Activities of Daily Living',
  self_care_independence: 'Self-Care Independence',
  client_rights: 'Client Rights',
  legal_ethical: 'Legal and Ethical',
  basic_nursing: 'Basic Nursing',
  health_care_team: 'Health Care Team',
  emotional_mental: 'Emotional and Mental Health',
  communication: 'Communication',
  spiritual_cultural: 'Spiritual and Cultural',
};

export function TrialResults() {
  const { sessionId = '' } = useParams();
  const { isAnonymous } = useAuth();

  const q = useQuery({
    queryKey: ['finish-trial', sessionId],
    queryFn: () => callFinishExam({ sessionId }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner className="h-8 w-8 text-brand-600 mx-auto mb-3" />
          <p className="text-sm text-stone-500">Calculating your readiness…</p>
        </div>
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card>
          <p className="text-rose-600 dark:text-rose-400 text-sm">
            {(q.error as { message?: string }).message ?? 'Could not load results.'}
          </p>
          <div className="mt-3">
            <Link to="/"><Button variant="secondary">Home</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  const data = q.data!;
  const score = Math.round((data.readinessReport?.score ?? data.score) * 100);
  const tone = data.readinessHeadline;
  const breakdown = data.readinessReport?.byCategory ?? {};

  // Sort categories by accuracy ascending → identify weakest/strongest.
  const categoryRows = Object.entries(breakdown)
    .filter(([, v]) => v && v.sampleSize > 0)
    .map(([cat, v]) => ({
      cat: cat as CategoryId,
      label: CATEGORY_LABEL[cat as CategoryId] ?? cat,
      acc: v!.rawAccuracy,
      sampleSize: v!.sampleSize,
      correct: Math.round(v!.rawAccuracy * v!.sampleSize),
    }))
    .sort((a, b) => b.acc - a.acc);
  const strong = categoryRows.filter((r) => r.acc >= 0.8);
  const weak = categoryRows.filter((r) => r.acc < 0.6);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100">
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-4 pb-24">
        {/* Hero score reveal */}
        <ScoreHero score={score} correct={data.correct} total={data.total} tone={tone} />

        {/* Strengths */}
        {strong.length > 0 && (
          <Card className="!p-4 sm:!p-5 border-emerald-200/70 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/60 to-white dark:from-emerald-900/15 dark:to-stone-950">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-[15px] font-semibold">You're already strong here</h2>
            </div>
            <ul className="space-y-1.5">
              {strong.map((r) => (
                <li key={r.cat} className="text-sm flex items-center justify-between gap-3">
                  <span className="truncate">{r.label}</span>
                  <span className="tabular-nums text-emerald-700 dark:text-emerald-400 font-semibold flex-none">
                    {Math.round(r.acc * 100)}%<span className="text-stone-400 font-normal ml-1.5">{r.correct}/{r.sampleSize}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Weaknesses */}
        {weak.length > 0 && (
          <Card className="!p-4 sm:!p-5 border-amber-200/70 dark:border-amber-900/50 bg-gradient-to-br from-amber-50/50 to-white dark:from-amber-900/15 dark:to-stone-950">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <h2 className="text-[15px] font-semibold">Where to focus next</h2>
            </div>
            <p className="text-[12.5px] text-stone-600 dark:text-stone-400 mb-2.5">
              Drilling these moves your overall score the fastest.
            </p>
            <ul className="space-y-1.5">
              {weak.map((r) => (
                <li key={r.cat} className="text-sm flex items-center justify-between gap-3">
                  <span className="truncate">{r.label}</span>
                  <span className="tabular-nums text-amber-700 dark:text-amber-400 font-semibold flex-none">
                    {Math.round(r.acc * 100)}%<span className="text-stone-400 font-normal ml-1.5">{r.correct}/{r.sampleSize}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* All-categories breakdown */}
        <Card className="!p-4 sm:!p-5">
          <h2 className="text-[15px] font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-stone-500" />
            All categories
          </h2>
          <ul className="space-y-2.5">
            {categoryRows.map((r) => (
              <li key={r.cat}>
                <div className="flex items-center justify-between text-[13.5px]">
                  <span className="truncate">{r.label}</span>
                  <span className="tabular-nums text-stone-500 flex-none ml-2">
                    {Math.round(r.acc * 100)}%
                  </span>
                </div>
                <div className="h-1.5 mt-1 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-700',
                      r.acc >= 0.8 ? 'bg-emerald-500' : r.acc >= 0.6 ? 'bg-brand-500' : 'bg-amber-500',
                    )}
                    style={{ width: `${Math.max(4, r.acc * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {/* Paywall — variant depends on auth state */}
        {isAnonymous ? <AnonymousPaywall score={score} /> : <RegisteredPaywall />}
      </main>
    </div>
  );
}

// ============================================================
//  Score hero — animated count-up + tone-coloured gradient
// ============================================================

interface ToneMeta {
  bg: string;
  ring: string;
  pill: string;
  numColor: string;
  label: string;
  body: string;
  emoji: string;
}

function ScoreHero({
  score,
  correct,
  total,
  tone,
}: {
  score: number;
  correct: number;
  total: number;
  tone?: { headline?: string; body?: string } | null;
}) {
  const t = scoreTone(score);

  // Count-up animation: 0 → score over ~1.2 s
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const duration = 1200;
    let raf = 0;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min(1, elapsed / duration);
      // Ease-out cubic for a satisfying decel.
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(score * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  return (
    <div className={cn('relative overflow-hidden rounded-2xl border p-6 sm:p-8 text-center', t.bg)}>
      {/* Pill */}
      <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wider', t.pill)}>
        {t.emoji} {t.label}
      </span>

      <p className="mt-3 text-xs uppercase tracking-wider text-stone-500">Your readiness score</p>
      <p className={cn('mt-1 text-7xl sm:text-8xl font-extrabold tabular-nums leading-none', t.numColor)}>
        {display}<span className="text-4xl sm:text-5xl">%</span>
      </p>
      <p className="mt-2 text-sm text-stone-700 dark:text-stone-300">
        <strong>{correct} of {total}</strong> correct
      </p>

      {/* Tone narrative */}
      {(tone?.headline || tone?.body) && (
        <div className="mt-5 max-w-md mx-auto">
          {tone.headline && (
            <p className="text-[15px] sm:text-base font-semibold text-stone-900 dark:text-stone-100">{tone.headline}</p>
          )}
          {tone.body && (
            <p className="text-[13px] sm:text-sm text-stone-600 dark:text-stone-400 mt-1">{tone.body}</p>
          )}
        </div>
      )}

      {/* Pass-threshold context */}
      <p className="mt-4 text-[11.5px] text-stone-500">
        {score >= 75
          ? '✓ You\'re above the typical 75% passing threshold.'
          : `${75 - score}% from the typical 75% pass threshold — closeable with focused drills.`}
      </p>
    </div>
  );
}

function scoreTone(score: number): ToneMeta {
  if (score >= 85) return {
    bg: 'border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 dark:from-emerald-900/30 dark:via-stone-950 dark:to-emerald-900/15',
    ring: 'stroke-emerald-500',
    pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    numColor: 'text-emerald-700 dark:text-emerald-400',
    label: 'Exam-ready',
    body: 'Strong across the board. You\'re prepared to test.',
    emoji: '🏆',
  };
  if (score >= 70) return {
    bg: 'border-brand-200 dark:border-brand-800 bg-gradient-to-br from-brand-50 via-white to-emerald-50/40 dark:from-brand-900/30 dark:via-stone-950 dark:to-emerald-900/20',
    ring: 'stroke-brand-500',
    pill: 'bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300',
    numColor: 'text-brand-700 dark:text-brand-400',
    label: 'Almost there',
    body: 'You\'re in the passing zone. A few focused drills will lock it in.',
    emoji: '🎯',
  };
  if (score >= 50) return {
    bg: 'border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 via-white to-brand-50/30 dark:from-amber-900/30 dark:via-stone-950 dark:to-brand-900/15',
    ring: 'stroke-amber-500',
    pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    numColor: 'text-amber-700 dark:text-amber-400',
    label: 'Closing the gap',
    body: 'You have the foundation. Daily practice on weak areas closes the gap fast.',
    emoji: '📈',
  };
  return {
    bg: 'border-rose-200 dark:border-rose-800 bg-gradient-to-br from-rose-50 via-white to-amber-50/30 dark:from-rose-900/30 dark:via-stone-950 dark:to-amber-900/15',
    ring: 'stroke-rose-500',
    pill: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
    numColor: 'text-rose-700 dark:text-rose-400',
    label: 'Building foundation',
    body: 'Every drill from here moves the needle. We\'ll show you exactly where to start.',
    emoji: '🌱',
  };
}

// ============================================================
//  Anonymous paywall — primary signup CTA
// ============================================================

function AnonymousPaywall({ score }: { score: number }) {
  const { upgradeAnonToEmail, upgradeAnonToGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async (e: FormEvent) => {
      e.preventDefault();
      setErr(null);
      setBusy(true);
      try {
        await upgradeAnonToEmail(email, password);
        nav('/verify-email');
      } catch (e) {
        setErr(friendly(e));
        throw e;
      } finally {
        setBusy(false);
      }
    },
  });

  async function onGoogle() {
    setErr(null);
    setBusy(true);
    try {
      await upgradeAnonToGoogle();
      nav('/dashboard');
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="!p-5 sm:!p-6 bg-gradient-to-br from-brand-50/60 via-white to-emerald-50/30 dark:from-brand-900/20 dark:via-stone-950 dark:to-emerald-900/15 border-brand-200 dark:border-brand-800">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-brand-600 dark:text-brand-400" />
        <h2 className="text-xl font-bold">
          {score >= 75 ? 'Lock in your readiness' : 'Get exam-ready in days, not weeks'}
        </h2>
      </div>
      <p className="text-[13.5px] text-stone-600 dark:text-stone-400 mb-4">
        {score >= 75
          ? 'You\'re close to passing — keep your edge with daily practice and a personalized study plan.'
          : 'Free account. No card. Get a personalized study plan based on your trial — start drilling your weak areas right away.'}
      </p>

      {/* Value props as a tight grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5 text-[13px]">
        {[
          { icon: <Zap className="h-3.5 w-3.5" />, label: 'Personalized study plan' },
          { icon: <BookOpen className="h-3.5 w-3.5" />, label: 'Unlimited practice questions' },
          { icon: <Trophy className="h-3.5 w-3.5" />, label: 'Full Practice Exams' },
          { icon: <Activity className="h-3.5 w-3.5" />, label: 'Progress over time' },
          { icon: <Award className="h-3.5 w-3.5" />, label: 'Save this trial result' },
          { icon: <Clock className="h-3.5 w-3.5" />, label: 'Resume any session' },
        ].map((p) => (
          <div key={p.label} className="flex items-center gap-2 text-stone-700 dark:text-stone-200">
            <span className="flex-none inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300">
              {p.icon}
            </span>
            <span>{p.label}</span>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => submit.mutate(e)} className="space-y-3">
        <div>
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="signup-pw">Password (≥ 6 characters)</Label>
          <Input
            id="signup-pw"
            type="password"
            autoComplete="new-password"
            minLength={6}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
        <Button type="submit" size="lg" loading={busy} className="w-full shadow-sm">
          Create free account & save my results
        </Button>
      </form>

      <div className="my-3 flex items-center gap-3 text-xs text-stone-500">
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
        <span>or</span>
        <span className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
      </div>
      <Button variant="secondary" size="lg" loading={busy} onClick={onGoogle} className="w-full">
        Continue with Google
      </Button>

      <p className="mt-4 text-center text-[12px] text-stone-500">
        <Lock className="inline h-3 w-3 mr-1 align-[-2px]" />
        We never share your info. Free account, no card needed.
      </p>
      <p className="mt-2 text-center text-[12px] text-stone-500">
        Already have an account?{' '}
        <Link to="/signin" className="text-brand-700 dark:text-brand-400 font-medium">
          Sign in
        </Link>
      </p>
    </Card>
  );
}

// ============================================================
//  Registered paywall — subscribe CTA
// ============================================================

function RegisteredPaywall() {
  return (
    <Card className="!p-5 sm:!p-6 bg-gradient-to-br from-brand-50/60 via-white to-emerald-50/30 dark:from-brand-900/20 dark:via-stone-950 dark:to-emerald-900/15 border-brand-200 dark:border-brand-800">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-brand-600 dark:text-brand-400" />
        <h2 className="text-xl font-bold">Unlock full access</h2>
      </div>
      <p className="text-[13.5px] text-stone-600 dark:text-stone-400 mb-4">
        Subscribers get unlimited Practice Exams, Revision Mode, and quick category practice.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5 text-[13px]">
        {[
          'Unlimited practice questions',
          'Unlimited Full Practice Exams',
          'Personalized study plan',
          'Smart readiness predictor',
          'Category drill mode',
          'Progress analytics',
        ].map((line) => (
          <div key={line} className="flex items-center gap-2 text-stone-700 dark:text-stone-200">
            <span className="flex-none inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300">
              ✓
            </span>
            <span>{line}</span>
          </div>
        ))}
      </div>
      <Button size="lg" disabled className="w-full">
        Subscribe — coming soon
      </Button>
      <p className="mt-3 text-xs text-stone-500">
        Stripe checkout will be enabled once your billing keys are wired. For now your dashboard is open below.
      </p>
      <div className="mt-4">
        <Link to="/dashboard">
          <Button variant="secondary" size="md" className="w-full">
            Go to dashboard
          </Button>
        </Link>
      </div>
    </Card>
  );
}
